'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    deleteHrEmployee,
    getHrEmployees,
    HrEmployee,
} from '@/lib/api/hr/hr-employee-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdArrowForward,
    MdBadge,
    MdClose,
    MdDelete,
    MdEdit,
    MdSearch,
    MdVisibility,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const STATUS_LABELS: Record<string, string> = {
    ACTIVE: 'Ativo',
    ON_LEAVE: 'Afastado',
    SUSPENDED: 'Suspenso',
    TERMINATED: 'Desligado',
}

const STATUS_BADGE: Record<string, string> = {
    ACTIVE: 'badge-success text-white',
    ON_LEAVE: 'badge-warning',
    SUSPENDED: 'badge-error text-white',
    TERMINATED: 'badge-ghost',
}

export default function HrEmployeesListPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'hr',
        'employees'
    )

    const [employees, setEmployees] = useState<HrEmployee[]>([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [departmentFilter, setDepartmentFilter] = useState('')
    const [page, setPage] = useState(1)
    const [limit] = useState(12)

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [employeeToDelete, setEmployeeToDelete] = useState<HrEmployee | null>(
        null
    )
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadEmployees()
        }
    }, [user?.companyId, page, searchTerm, statusFilter, departmentFilter])

    const loadEmployees = async () => {
        setLoading(true)
        try {
            const response = await getHrEmployees({
                search: searchTerm || undefined,
                status: statusFilter || undefined,
                department: departmentFilter || undefined,
                page,
                limit,
            })
            if (response.success && response.result) {
                const all = Array.isArray(response.result)
                    ? response.result
                    : []
                setEmployees(all)
                setTotal(all.length)
            } else {
                toast.error(response.error || 'Erro ao carregar funcionários')
            }
        } catch {
            toast.error('Erro ao carregar funcionários')
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
        setPage(1)
    }

    const handleDelete = async () => {
        if (!employeeToDelete) return

        setDeleting(true)
        try {
            const response = await deleteHrEmployee(employeeToDelete.id)
            if (response.success) {
                toast.success('Funcionário excluído com sucesso')
                setShowDeleteModal(false)
                setEmployeeToDelete(null)
                await loadEmployees()
            } else {
                toast.error(response.error || 'Erro ao excluir funcionário')
            }
        } catch {
            toast.error('Erro ao excluir funcionário')
        } finally {
            setDeleting(false)
        }
    }

    const formatCurrency = (cents: number | null) => {
        if (cents == null) return '-'
        return (cents / 100).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        })
    }

    const formatDate = (date: string | null) => {
        if (!date) return '-'
        return new Intl.DateTimeFormat('pt-BR').format(new Date(date))
    }

    const totalPages = Math.ceil(total / limit)

    if (loading && employees.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 animate-fade-in">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando funcionários...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-neutral to-neutral/70 flex items-center justify-center shadow-lg">
                        <MdBadge className="text-2xl text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-bold text-base-content">
                                Funcionários
                            </h1>
                            <span className="badge badge-secondary badge-sm">
                                {total}
                            </span>
                        </div>
                        <p className="text-sm text-base-content/60">
                            Gestão de funcionários da empresa
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() => navigate('/hr/funcionarios/new')}
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">
                            Novo Funcionário
                        </span>
                    </button>
                )}
            </div>

            {/* Filters */}
            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <form
                    onSubmit={handleSearch}
                    className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
                >
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, CPF ou cargo..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => {
                                setSearchTerm(e.target.value)
                                setPage(1)
                            }}
                        />
                    </div>
                    <select
                        className="select select-bordered w-full sm:w-44"
                        value={statusFilter}
                        onChange={e => {
                            setStatusFilter(e.target.value)
                            setPage(1)
                        }}
                    >
                        <option value="">Todos os status</option>
                        <option value="ACTIVE">Ativo</option>
                        <option value="ON_LEAVE">Afastado</option>
                        <option value="SUSPENDED">Suspenso</option>
                        <option value="TERMINATED">Desligado</option>
                    </select>
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Departamento"
                            className="input input-bordered w-full sm:w-44"
                            value={departmentFilter}
                            onChange={e => {
                                setDepartmentFilter(e.target.value)
                                setPage(1)
                            }}
                        />
                    </div>
                    {(searchTerm || statusFilter || departmentFilter) && (
                        <button
                            type="button"
                            onClick={() => {
                                setSearchTerm('')
                                setStatusFilter('')
                                setDepartmentFilter('')
                                setPage(1)
                            }}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                            Limpar
                        </button>
                    )}
                </form>
            </div>

            {/* Cards */}
            {employees.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdBadge className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm || statusFilter || departmentFilter
                                ? 'Nenhum funcionário encontrado'
                                : 'Nenhum funcionário cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm || statusFilter || departmentFilter
                                ? 'Tente ajustar os filtros de busca'
                                : 'Comece adicionando funcionários para que eles apareçam aqui.'}
                        </p>
                        {!searchTerm &&
                            !statusFilter &&
                            !departmentFilter &&
                            canCreate && (
                                <button
                                    onClick={() =>
                                        navigate('/hr/funcionarios/new')
                                    }
                                    className="btn btn-secondary gap-2"
                                >
                                    <MdAdd className="text-xl" />
                                    Adicionar primeiro funcionário
                                </button>
                            )}
                    </div>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {employees.map(employee => (
                            <div
                                key={employee.id}
                                className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                                onClick={() =>
                                    navigate(`/hr/funcionarios/${employee.id}`)
                                }
                            >
                                <div className="p-4">
                                    <div className="flex items-start justify-between gap-2 mb-3">
                                        <div className="min-w-0">
                                            <h3 className="text-base font-bold text-base-content line-clamp-1 group-hover:text-secondary transition-colors">
                                                {employee.name}
                                            </h3>
                                            {employee.jobTitle && (
                                                <p className="text-sm text-base-content/60 line-clamp-1">
                                                    {employee.jobTitle}
                                                </p>
                                            )}
                                        </div>
                                        <span
                                            className={`badge badge-sm flex-shrink-0 ${
                                                STATUS_BADGE[employee.status] ||
                                                'badge-ghost'
                                            }`}
                                        >
                                            {STATUS_LABELS[employee.status] ||
                                                employee.status}
                                        </span>
                                    </div>

                                    <div className="space-y-1.5 text-sm mb-3">
                                        {employee.cpf && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-base-content/50">
                                                    CPF
                                                </span>
                                                <span className="text-base-content/80">
                                                    {employee.cpf}
                                                </span>
                                            </div>
                                        )}
                                        {employee.department && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-base-content/50">
                                                    Departamento
                                                </span>
                                                <span className="text-base-content/80">
                                                    {employee.department}
                                                </span>
                                            </div>
                                        )}
                                        {/* A lista é da organização inteira, então a
                                            filial precisa aparecer: é ela que emprega,
                                            declara o eSocial e paga a folha. */}
                                        {employee.company?.name && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-base-content/50">
                                                    Filial
                                                </span>
                                                <span className="text-base-content/80">
                                                    {employee.company.name}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between">
                                            <span className="text-base-content/50">
                                                Salário
                                            </span>
                                            <span className="font-medium text-base-content">
                                                {formatCurrency(
                                                    employee.salary
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-base-content/50">
                                                Admissão
                                            </span>
                                            <span className="text-base-content/80">
                                                {formatDate(
                                                    employee.admissionDate
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                        <div className="flex gap-1">
                                            <button
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    navigate(
                                                        `/hr/funcionarios/${employee.id}`
                                                    )
                                                }}
                                                className="btn btn-ghost btn-xs gap-1"
                                                title="Ver detalhes"
                                            >
                                                <MdVisibility className="text-base" />
                                            </button>
                                            {canUpdate && (
                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation()
                                                        navigate(
                                                            `/hr/funcionarios/${employee.id}/edit`
                                                        )
                                                    }}
                                                    className="btn btn-ghost btn-xs gap-1"
                                                    title="Editar"
                                                >
                                                    <MdEdit className="text-base" />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation()
                                                        setEmployeeToDelete(
                                                            employee
                                                        )
                                                        setShowDeleteModal(true)
                                                    }}
                                                    className="btn btn-ghost btn-xs text-error gap-1"
                                                    title="Excluir"
                                                >
                                                    <MdDelete className="text-base" />
                                                </button>
                                            )}
                                        </div>
                                        <MdArrowForward className="text-lg text-base-content/30 group-hover:text-secondary group-hover:translate-x-1 transition-all" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 pt-2">
                            <button
                                className="btn btn-sm btn-ghost"
                                disabled={page <= 1}
                                onClick={() => setPage(p => p - 1)}
                            >
                                <MdArrowBack className="text-lg" />
                            </button>
                            <span className="text-sm text-base-content/60">
                                Página {page} de {totalPages}
                            </span>
                            <button
                                className="btn btn-sm btn-ghost"
                                disabled={page >= totalPages}
                                onClick={() => setPage(p => p + 1)}
                            >
                                <MdArrowForward className="text-lg" />
                            </button>
                        </div>
                    )}
                </>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setEmployeeToDelete(null)
                }}
                onConfirm={handleDelete}
                title="Excluir funcionário"
                message={`Tem certeza de que deseja excluir o funcionário "${employeeToDelete?.name}"? Esta ação não pode ser desfeita.`}
                loading={deleting}
                confirmButtonText="Excluir"
            />
        </div>
    )
}
